<script setup lang="ts">
/* eslint-disable vue/no-v-html */
import type { PageHeaderMenuItem } from '~/components/page/header/menu/PageHeaderMenu.vue'
import type { ApiInsuranceProduct } from '~~/server/api/insurance/product'

export type DocumentGroupsItem = {
    title: string;
    documents: DocumentGroup[];
};

const route = useRoute()
const { data: page, redirect } =
    await useFetchWithRedirect<ApiInsuranceProduct>(
        `/api/insurance/product?slug=${route.params.id}`,
        { route },
        {
            params: {
                t: route.query.t || '',
            },
        }
    )

if (redirect && !redirect?.error) {
    navigateTo(redirect.data.url, redirect.data.options)
}

const category = computed<string>((): string => {
    if (page.value?.data?.breadcrumbs?.length) {
        const lastBreadcrumb = page.value?.data?.breadcrumbs?.length - 1

        return page.value?.data?.breadcrumbs?.[lastBreadcrumb]?.slug
    }

    return ''
})
const { $t } = useNuxtApp()
const insuranceNamedRoute = useNamedRoutes({ name: 'insurance-categories' })
const breadcrumbs = computed<BreadcrumbsLink[]>(() => {
    if (!page.value) return []

    const links: BreadcrumbsLink[] = page.value.data.breadcrumbs
        .slice(1)
        .map((link: BreadcrumbsLink): BreadcrumbsLink => {
            return {
                name: link.name,
                slug: `${insuranceNamedRoute}/${link.slug}`,
            }
        })

    return [
        {
            name: page.value.data.breadcrumbs[0]?.name || '',
            slug: insuranceNamedRoute,
        },
        ...links,
    ]
})
const sections = computed<PageHeaderMenuItem[]>(() => {
    if (!page.value) return []

    const menu: PageHeaderMenuItem[] = [{ title: 'Úvod', hash: '#uvod' }]

    if (page.value?.data.benefit_groups?.data.length) {
        menu.push({
            title: $t('O poistení'),
            hash: '#o-poisteni',
        })
    }
    if (page.value?.data.bundle) {
        menu.push({ title: $t('Balíky'), hash: '#baliky' })
    }
    if (page.value.data.additional_insurances?.data.length) {
        menu.push({ title: $t('Pripoistenia'), hash: '#pripoistenia' })
    }
    if (page.value.data.assistance_service?.data.length) {
        menu.push({ title: 'Služby v cene', hash: '#sluzby-v-cene' })
    }
    if (page.value.data.faq?.data.length) {
        menu.push({
            title: $t('Otázky a odpovede'),
            hash: '#otazky-a-odpovede',
        })
    }
    menu.push({ title: $t('Dokumenty'), hash: '#dokumenty' })

    return menu
})

const documentGroups = computed<DocumentGroupsItem[]>(
    (): DocumentGroupsItem[] => {
        if (!page.value) return []

        return [
            {
                title: $t('Predzmluvná dokumentácia'),
                documents: page.value.data
                    .precontract_documentation as unknown as DocumentGroup[],
            },
            {
                title: $t('Poistné podmienky'),
                documents: page.value.data.insurance_conditions,
            },
            {
                title: $t('Ostatné dokumenty'),
                documents: page.value.data.other_documents,
            },
        ]
    }
)

if (route.params.category !== category.value) {
    useCanonicalHead({ params: { category: category.value } })
} else {
    useCanonicalHead({})
}

// Schema.org
useSchemaOrg([
    defineProduct({
        name: page.value?.data.title,
        category: page.value?.data.category.title,
        image: page.value?.data.headline.image.path['1540x0']?.url || '',
    }),
])

// Page meta
usePageMeta({
    meta: page.value?.data.meta,
    title: page.value?.data.title,
    og_type: 'product',
})
</script>
<template>
  <article v-if="page">
    <ContainerFluid>
      <Breadcrumbs
        :current="page.data.title"
        :links="breadcrumbs"
      />
    </ContainerFluid>
    <PageHeader
      class="mb-12"
      :headline="page.data.headline"
      :title="page.data.title"
      :sections="sections"
      :cta="page.data.headline.cta"
      :subtitle="page.data.subtitle"
    />
    <div class="content-visibility-auto">
      <ContainerFluid>
        <ListIcons
          v-if="page.data.top_benefits"
          class="mb-12 mb-md-20 scroll-margin-top-12"
          :data="page.data.top_benefits"
        />
        <PagesInsuranceDetailIntro
          class="mb-12 mb-md-20 scroll-margin-top-10"
          :data="(page.data as any)"
        />
        <LazyWidgetInsuranceServices
          v-if="page.data.benefit_groups?.data.length > 0"
          id="o-poisteni"
          class="mb-12 mb-md-20 scroll-margin-top-17"
          :data="page.data.benefit_groups"
          data-cy="insurance_benefits"
        />
        <template v-if="page.data.bundle?.length">
          <LazyPackageList
            v-for="(bundle, index) in page.data.bundle"
            id="baliky"
            :key="`package-${index}`"
            :packages="bundle"
            class="mb-12 mb-md-20 scroll-margin-top-15"
          />
        </template>
        <LazyWidgetHelpdesk
          class="mb-12 mb-md-20 scroll-margin-top-10"
          :icon-size="22"
          :icon-wrapper-size="56"
        >
          <template #title>
            {{ $t("Neviete si vybrať?") }}
          </template>
          <template #subtitle>
            {{ $t("Kontaktujte nás") }}
          </template>
        </LazyWidgetHelpdesk>
        <LazyWidgetInsuranceAdditional
          v-if="page.data.additional_insurances?.data.length"
          id="pripoistenia"
          heading-level="h2"
          class="mb-12 mb-md-20 scroll-margin-top-17"
          :data="page.data.additional_insurances"
        />
        <LazyWidgetPages
          v-if="page.data.assistance_service?.data.length"
          id="sluzby-v-cene"
          :data="page.data.assistance_service"
          class="mb-12 mb-md-20 scroll-margin-top-15"
        />
        <LazyGallery
          v-if="page.data.gallery?.data[0]?.file?.path"
          class="mb-12 mb-md-20 scroll-margin-top-10"
          :data="page.data.gallery"
          :thumbnail="page.data.gallery.data[0].file.path"
          :extra="page.data.gallery?.data[0]?.title"
          heading-level="h2"
        />
        <LazyVVideo
          v-if="page.data.video"
          heading-level="h2"
          class="pb-12 pb-md-14"
          :data="page.data.video"
        />
        <LazyBanner
          v-if="page.data.banner"
          :data="page.data.banner"
          heading-level="h2"
          class="mb-12 mb-md-20"
        >
          <template
            v-if="
              page.data.banner.tag.background &&
                page.data.banner.tag.name
            "
            #tag
          >
            <Label
              :text-color="page.data.banner.tag.color"
              :label="page.data.banner.tag.name"
              :background-color="page.data.banner.tag.background"
            />
          </template>
        </LazyBanner>
        <LazyWidgetArticles
          v-if="page.data.blog_posts.data.length"
          class="mb-12 mb-md-20 scroll-margin-top-10"
          heading-level="h2"
          :data="page.data.blog_posts"
          :heading-override="$t('Najnovšie články')"
        />
        <LazyWidgetFaq
          v-if="page.data.faq.data.length"
          id="otazky-a-odpovede"
          :data="page.data.faq"
          :rating="page.data.product_rating"
          heading-level="h2"
          :heading-override="$t('Najčastejšie otázky a odpovede')"
          collapsable
          class="mb-12 mb-md-20 scroll-margin-top-15"
        />
        <LazyWidgetDocuments
          id="dokumenty"
          class="mb-12 mb-md-20 scroll-margin-top-17"
          :data="documentGroups"
        />
        <div
          v-if="page.data.seo_article"
          class="mt-12 mt-md-20"
        >
          <LazySeoArticle :content="page.data.seo_article" />
        </div>

        <div class="mt-12 mt-md-20 pt-12 pt-md-20 border-top">
          <LazyWidgetHelpdeskExt />
        </div>
      </ContainerFluid>
    </div>
  </article>
</template>
